import React, { useEffect, useMemo, useState } from "react";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Trash2, Shield, User as UserIcon, Ban, RotateCcw, Search, Users as UsersIcon, LayoutGrid, List as ListIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import TierBadge from "@/components/TierBadge";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLE_OPTS = [
  { value: "all", label: "Tutti i ruoli" },
  { value: "admin", label: "Solo admin" },
  { value: "user", label: "Solo utenti" },
];
const STATUS_OPTS = [
  { value: "all", label: "Tutti gli stati" },
  { value: "active", label: "Attivi" },
  { value: "blocked", label: "Bloccati" },
  { value: "online", label: "Online adesso" },
  { value: "demo", label: "Solo Demo" },
  { value: "subscriber", label: "Solo Abbonati" },
];

const AdminUsers = ({ embedded = false }) => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);
  const [toBlock, setToBlock] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [view, setView] = useState("cards"); // "cards" | "table" | "list"

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/users"); setUsers(r.data); }
    catch (e) { toast.error("Errore caricamento utenti"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // Refresh online status every 20s
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (statusFilter === "active" && u.blocked) return false;
      if (statusFilter === "blocked" && !u.blocked) return false;
      if (statusFilter === "online" && !u.online) return false;
      if (statusFilter === "demo" && (u.status || "demo") !== "demo") return false;
      if (statusFilter === "subscriber" && u.status !== "subscriber") return false;
      if (tierFilter !== "all" && (u.tier || "demo") !== tierFilter) return false;
      if (cityFilter !== "all" && ((u.city || "").toLowerCase() !== cityFilter.toLowerCase())) return false;
      if (q) {
        const hay = `${u.email || ""} ${u.name || ""} ${u.first_name || ""} ${u.last_name || ""} ${u.phone || ""} ${u.address || ""} ${u.cap || ""} ${u.city || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, query, roleFilter, statusFilter, tierFilter, cityFilter]);

  // Distinct city list built from the actual user documents — populates
  // the city filter dropdown so admins can pick from what really exists.
  const cityOpts = useMemo(() => {
    const set = new Set();
    users.forEach((u) => { if (u.city) set.add(u.city.trim()); });
    return [...set].sort((a, b) => a.localeCompare(b, "it"));
  }, [users]);

  const allSelectableIds = useMemo(
    () => filtered.filter((u) => u.user_id !== user?.user_id).map((u) => u.user_id),
    [filtered, user]
  );
  const allSelected = allSelectableIds.length > 0 && allSelectableIds.every((id) => selected.has(id));

  if (user && user.role !== "admin" && !embedded) return <Navigate to="/boards" replace />;

  const toggleOne = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected((s) => {
      if (allSelected) return new Set();
      return new Set(allSelectableIds);
    });
  };

  const changeRole = async (u, role) => {
    try { await api.put(`/users/${u.user_id}/role`, { role }); toast.success("Ruolo aggiornato"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const changeTier = async (u, tier) => {
    try { await api.put(`/users/${u.user_id}/tier`, { tier }); toast.success(`Tipologia impostata: ${tier}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const changeStatus = async (u, status) => {
    try {
      await api.put(`/users/${u.user_id}/status`, { status });
      toast.success(status === "subscriber" ? "Utente attivato come Abbonato" : "Utente riportato in Demo");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // Colored dot + label for tier badge — reused both in grid and table view.
  const TierBadge = ({ tier }) => {
    const t = tier || "demo";
    const map = { demo: "#8E8E93", rookie: "#0A84FF", pro: "#F59E0B" };
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest">
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: map[t] || map.demo }} />
        {t}
      </span>
    );
  };

  const confirmDelete = async () => {
    try { await api.delete(`/users/${toDelete.user_id}`); toast.success("Utente eliminato"); setToDelete(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const confirmBlock = async () => {
    try {
      if (toBlock.blocked) {
        await api.put(`/users/${toBlock.user_id}/unblock`); toast.success("Utente sbloccato");
      } else {
        await api.put(`/users/${toBlock.user_id}/block`); toast.success("Utente bloccato");
      }
      setToBlock(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const confirmBulk = async () => {
    try {
      const ids = Array.from(selected);
      const r = await api.post("/users/bulk-delete", { user_ids: ids });
      const msg = `${r.data.deleted} eliminati` + (r.data.skipped_admins?.length ? ` · ${r.data.skipped_admins.length} admin di sistema saltati` : "");
      toast.success(msg);
      setSelected(new Set());
      setBulkOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione multipla");
    }
  };

  const clearFilters = () => { setQuery(""); setRoleFilter("all"); setStatusFilter("all"); setTierFilter("all"); setCityFilter("all"); };
  const filtersActive = query.trim() || roleFilter !== "all" || statusFilter !== "all" || tierFilter !== "all" || cityFilter !== "all";

  return (
    <div className={embedded ? "" : "min-h-screen"}>
      {!embedded && <AppHeader />}
      <div className={embedded ? "" : "max-w-7xl mx-auto p-6"}>
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            {!embedded && <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Amministrazione</div>}
            {!embedded && <h1 className="font-display font-bold text-4xl tracking-tighter leading-none mt-1">Gestione utenti</h1>}
            <p className="text-sm text-slate-500 mt-2 max-w-2xl">
              Filtra, seleziona e gestisci gli utenti. Il riquadro verde fluo indica chi è <b>online adesso</b>. Le schede degli utenti bloccati/rimossi vanno in <b>Schede vacanti</b>.
            </p>
          </div>
          <div className="ios-segmented" data-testid="admin-view-toggle">
            <button className={view === "cards" ? "active" : ""} onClick={() => setView("cards")} data-testid="view-cards">
              <LayoutGrid className="w-3.5 h-3.5 inline mr-1" /> Schede
            </button>
            <button className={view === "list" ? "active" : ""} onClick={() => setView("list")} data-testid="view-list-users">
              <ListIcon className="w-3.5 h-3.5 inline mr-1" /> Elenco
            </button>
            <button className={view === "table" ? "active" : ""} onClick={() => setView("table")} data-testid="view-table-users">
              <ListIcon className="w-3.5 h-3.5 inline mr-1" /> Tabella
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="ios-card p-3 mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="user-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per nome o email…"
              className="rounded-xl bg-slate-100 border-0 pl-9 h-9"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger data-testid="role-filter" className="w-40 h-9 rounded-xl bg-slate-100 border-0"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLE_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="status-filter" className="w-44 h-9 rounded-xl bg-slate-100 border-0"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tierFilter} onValueChange={setTierFilter}>
            <SelectTrigger data-testid="tier-filter" className="w-36 h-9 rounded-xl bg-slate-100 border-0"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i piani</SelectItem>
              <SelectItem value="demo">Demo</SelectItem>
              <SelectItem value="bronze">Bronze</SelectItem>
              <SelectItem value="silver">Silver</SelectItem>
              <SelectItem value="gold">Gold</SelectItem>
            </SelectContent>
          </Select>
          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger data-testid="city-filter" className="w-40 h-9 rounded-xl bg-slate-100 border-0"><SelectValue placeholder="Città" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le città</SelectItem>
              {cityOpts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {filtersActive && (
            <Button variant="ghost" className="rounded-full h-9 text-slate-500" onClick={clearFilters} data-testid="clear-filters">Reset</Button>
          )}
          <div className="ml-auto flex items-center gap-3 text-[11px] uppercase tracking-wider text-slate-500">
            <UsersIcon className="w-3.5 h-3.5" /> {filtered.length}/{users.length}
          </div>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="ios-card px-4 py-2 mb-4 flex items-center justify-between bg-[#007AFF]/5 border border-[#007AFF]/20" data-testid="bulk-bar">
            <div className="text-sm">
              <b>{selected.size}</b> utente{selected.size === 1 ? "" : "i"} selezionato{selected.size === 1 ? "" : "i"}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="rounded-full h-8 text-slate-500" onClick={() => setSelected(new Set())} data-testid="bulk-clear">
                Deseleziona
              </Button>
              <Button size="sm" className="rounded-full h-8 bg-rose-600 hover:bg-rose-700 text-white" onClick={() => setBulkOpen(true)} data-testid="bulk-delete-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Elimina selezionati
              </Button>
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-16 text-slate-500 text-sm">Caricamento…</div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="ios-card text-center py-16 text-slate-500 text-sm">Nessun utente corrisponde ai filtri.</div>
        )}

        {/* Card view */}
        {!loading && view === "cards" && filtered.length > 0 && (
          <>
            {allSelectableIds.length > 0 && (
              <label className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-widest mb-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  className="w-4 h-4 rounded accent-[#007AFF]"
                  data-testid="select-all"
                />
                Seleziona tutti ({allSelectableIds.length})
              </label>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="users-grid">
              {filtered.map((u) => {
                const isSelf = u.user_id === user?.user_id;
                const isSelected = selected.has(u.user_id);
                const bg = u.picture || "";
                const isOnline = !!u.online;
                return (
                  <div
                    key={u.user_id}
                    data-testid={`user-card-${u.user_id}`}
                    className={`relative overflow-hidden rounded-2xl border transition-all ${
                      isOnline ? "border-emerald-400 shadow-[0_0_0_2px_rgba(52,199,89,0.35),0_0_28px_rgba(52,199,89,0.55)]" : "border-slate-200"
                    } ${isSelected ? "ring-2 ring-[#007AFF] ring-offset-2" : ""} ${u.blocked ? "opacity-80" : ""} bg-slate-800`}
                    style={{ minHeight: 240 }}
                  >
                    {/* Blurred, translucent background image */}
                    {bg && (
                      <div
                        aria-hidden
                        className="absolute inset-0"
                        style={{
                          backgroundImage: `url(${bg})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          filter: "blur(24px) saturate(1.1)",
                          opacity: 0.45,
                          transform: "scale(1.15)",
                        }}
                      />
                    )}
                    {/* Overlay for readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/40 to-slate-900/10" />

                    {/* Top row: checkbox + status pills */}
                    <div className="relative z-10 flex items-start justify-between p-3">
                      {!isSelf ? (
                        <label className="flex items-center gap-1.5 cursor-pointer bg-white/95 backdrop-blur px-2 py-1 rounded-full shadow" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleOne(u.user_id)}
                            className="w-3.5 h-3.5 rounded accent-[#007AFF]"
                            data-testid={`select-${u.user_id}`}
                          />
                          <span className="text-[10px] uppercase tracking-widest text-slate-700">Sel.</span>
                        </label>
                      ) : (
                        <Badge className="rounded-full bg-white/95 text-[9px] text-slate-700 uppercase tracking-widest border-0">Tu</Badge>
                      )}
                      <div className="flex flex-col items-end gap-1">
                        {isOnline && (
                          <Badge className="rounded-full bg-emerald-500 text-white text-[9px] uppercase tracking-widest border-0 shadow-[0_0_10px_rgba(52,199,89,0.7)] animate-pulse" data-testid={`online-${u.user_id}`}>
                            ● Online
                          </Badge>
                        )}
                        {u.blocked && (
                          <Badge className="rounded-full bg-rose-500 text-white text-[9px] uppercase tracking-widest border-0">Bloccato</Badge>
                        )}
                        {u.role === "admin" && (
                          <Badge className="rounded-full bg-[#12466F] text-white text-[9px] uppercase tracking-widest border-0"><Shield className="w-2.5 h-2.5 mr-1" />Admin</Badge>
                        )}
                        {u.role !== "admin" && (u.status === "subscriber" ? (
                          <Badge className="rounded-full bg-emerald-500 text-white text-[9px] uppercase tracking-widest border-0" data-testid={`status-badge-${u.user_id}`}>Abbonato</Badge>
                        ) : (
                          <Badge className="rounded-full bg-slate-500 text-white text-[9px] uppercase tracking-widest border-0" data-testid={`status-badge-${u.user_id}`}>Demo</Badge>
                        ))}
                      </div>
                    </div>

                    {/* Bottom content */}
                    <div className="absolute bottom-0 left-0 right-0 z-10 p-4 text-white">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="relative shrink-0">
                          {bg ? (
                            <img
                              src={bg}
                              alt=""
                              className={`w-12 h-12 rounded-full object-cover border-2 ${
                                isOnline ? "border-emerald-400 shadow-[0_0_12px_rgba(52,199,89,0.85)]" : "border-white/80"
                              }`}
                            />
                          ) : (
                          <div className={`w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-lg font-semibold text-white border-2 shrink-0 ${
                            isOnline ? "border-emerald-400 shadow-[0_0_12px_rgba(52,199,89,0.85)]" : "border-white/80"
                          }`}>
                            {(u.name || u.email)[0]?.toUpperCase()}
                          </div>
                        )}
                        <TierBadge tier={u.tier || "demo"} size={22} withGlow className="absolute -top-1 -right-1" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-display font-bold text-lg leading-tight truncate drop-shadow">{u.name || "—"}</div>
                          <div className="text-xs text-white/80 truncate">{u.email}</div>
                        </div>
                      </div>
                      <div className="text-[10px] text-white/60 uppercase tracking-widest">Reg. {(u.created_at || "").slice(0, 10)}</div>

                      <div className="flex items-center gap-2 mt-3">
                        <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.blocked || isSelf}>
                          <SelectTrigger className="h-7 w-28 rounded-full bg-white/95 text-slate-900 border-0 text-xs" data-testid={`role-${u.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin"><div className="flex items-center gap-2"><Shield className="w-3.5 h-3.5" /> Admin</div></SelectItem>
                            <SelectItem value="user"><div className="flex items-center gap-2"><UserIcon className="w-3.5 h-3.5" /> Utente</div></SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={u.tier || "demo"} onValueChange={(v) => changeTier(u, v)} disabled={u.blocked}>
                          <SelectTrigger className="h-7 w-24 rounded-full bg-white/95 text-slate-900 border-0 text-xs uppercase tracking-widest" data-testid={`tier-${u.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="demo">demo</SelectItem>
                            <SelectItem value="bronze">bronze</SelectItem>
                            <SelectItem value="silver">silver</SelectItem>
                            <SelectItem value="gold">gold</SelectItem>
                          </SelectContent>
                        </Select>
                        {u.role !== "admin" && (
                          <Select value={u.status || "demo"} onValueChange={(v) => changeStatus(u, v)} disabled={u.blocked}>
                            <SelectTrigger className="h-7 w-28 rounded-full bg-white/95 text-slate-900 border-0 text-xs" data-testid={`status-${u.user_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="demo">Demo</SelectItem>
                              <SelectItem value="subscriber">Abbonato</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        {!isSelf && (
                          <>
                            <Button size="sm" variant="ghost"
                              className={`rounded-full h-7 px-2 text-xs bg-white/95 hover:bg-white ${u.blocked ? "text-emerald-700" : "text-amber-700"}`}
                              onClick={() => setToBlock(u)}
                              data-testid={`block-${u.user_id}`}
                            >
                              {u.blocked ? <><RotateCcw className="w-3 h-3 mr-1" />Sblocca</> : <><Ban className="w-3 h-3 mr-1" />Blocca</>}
                            </Button>
                            <Button size="sm" variant="ghost" className="rounded-full h-7 w-7 p-0 bg-white/95 hover:bg-rose-50 text-rose-600"
                              onClick={() => setToDelete(u)} data-testid={`del-user-${u.user_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Table view */}
        {!loading && view === "list" && filtered.length > 0 && (
          <div className="space-y-2" data-testid="users-list">
            {filtered.map((u) => {
              const isSelf = u.user_id === user?.user_id;
              const isSelected = selected.has(u.user_id);
              const isOnline = !!u.online;
              return (
                <div
                  key={u.user_id}
                  data-testid={`user-list-${u.user_id}`}
                  className={`ios-card px-4 py-3 flex flex-wrap items-center gap-3 border ${isOnline ? "border-emerald-400/60 shadow-[0_0_0_1px_rgba(52,199,89,0.35)]" : "border-slate-200"} ${isSelected ? "ring-2 ring-[#007AFF] ring-offset-2" : ""} ${u.blocked ? "opacity-70" : ""}`}
                >
                  {!isSelf && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleOne(u.user_id)}
                      className="w-4 h-4 rounded accent-[#007AFF] shrink-0"
                      data-testid={`select-list-${u.user_id}`}
                    />
                  )}
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-10 h-10 rounded-full shrink-0 object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-200 text-sm font-semibold text-slate-600 flex items-center justify-center shrink-0">
                      {(u.name || u.email || "?")[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-[180px] flex-1">
                    <div className="font-semibold text-sm text-slate-900 flex items-center gap-2 flex-wrap">
                      {u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || "—"}
                      {isSelf && <Badge className="rounded-full bg-slate-100 text-slate-700 text-[9px] uppercase tracking-widest border-0">Tu</Badge>}
                      {isOnline && <Badge className="rounded-full bg-emerald-500 text-white text-[9px] uppercase tracking-widest border-0 animate-pulse">● Online</Badge>}
                      {u.blocked && <Badge className="rounded-full bg-rose-500 text-white text-[9px] uppercase tracking-widest border-0">Bloccato</Badge>}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-1 text-[11px] flex-1 min-w-[260px]">
                    <div><span className="text-slate-400 uppercase tracking-widest text-[9px] block">Telefono</span><span className="font-mono-num">{u.phone || "—"}</span></div>
                    <div className="sm:col-span-2"><span className="text-slate-400 uppercase tracking-widest text-[9px] block">Indirizzo</span><span className="truncate">{u.address || "—"}</span></div>
                    <div><span className="text-slate-400 uppercase tracking-widest text-[9px] block">CAP</span><span className="font-mono-num">{u.cap || "—"}</span></div>
                    <div><span className="text-slate-400 uppercase tracking-widest text-[9px] block">Città</span><span>{u.city || "—"}</span></div>
                    <div><span className="text-slate-400 uppercase tracking-widest text-[9px] block">Iscritto</span><span className="font-mono-num">{(u.created_at || "").slice(0, 10) || "—"}</span></div>
                    <div><span className="text-slate-400 uppercase tracking-widest text-[9px] block">Tier</span><TierBadge tier={u.tier || "demo"} /></div>
                    <div><span className="text-slate-400 uppercase tracking-widest text-[9px] block">Ruolo</span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold">
                        {u.role === "admin" ? <><Shield className="w-3 h-3 text-[#12466F]" />Admin</> : <><UserIcon className="w-3 h-3" />Utente</>}
                      </span>
                    </div>
                    <div><span className="text-slate-400 uppercase tracking-widest text-[9px] block">Stato</span>
                      <span className="text-[11px] font-semibold">{u.role === "admin" ? "Admin" : (u.status === "subscriber" ? "Abbonato" : "Demo")}</span>
                    </div>
                    <div><span className="text-slate-400 uppercase tracking-widest text-[9px] block">Auth</span><span className="text-[11px]">{u.auth_method || "google"}</span></div>
                    <div><span className="text-slate-400 uppercase tracking-widest text-[9px] block">Ultimo accesso</span><span className="font-mono-num">{(u.last_seen || "").slice(0, 16).replace("T", " ") || "—"}</span></div>
                  </div>

                  <div className="flex items-center gap-1.5 ml-auto shrink-0">
                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.blocked || isSelf}>
                      <SelectTrigger className="h-8 w-28 rounded-full bg-slate-50 border-slate-200 text-xs" data-testid={`role-list-${u.user_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="user">Utente</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={u.tier || "demo"} onValueChange={(v) => changeTier(u, v)} disabled={u.blocked}>
                      <SelectTrigger className="h-8 w-24 rounded-full bg-slate-50 border-slate-200 text-xs uppercase tracking-widest" data-testid={`tier-list-${u.user_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="demo">demo</SelectItem>
                        <SelectItem value="bronze">bronze</SelectItem>
                        <SelectItem value="silver">silver</SelectItem>
                        <SelectItem value="gold">gold</SelectItem>
                      </SelectContent>
                    </Select>
                    {!isSelf && (
                      <>
                        <Button size="sm" variant="ghost"
                          className={`rounded-full h-8 px-2 text-xs bg-slate-50 hover:bg-slate-100 ${u.blocked ? "text-emerald-700" : "text-amber-700"}`}
                          onClick={() => setToBlock(u)} data-testid={`block-list-${u.user_id}`}
                        >
                          {u.blocked ? <RotateCcw className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-full h-8 w-8 p-0 text-rose-600 hover:bg-rose-50"
                          onClick={() => setToDelete(u)} data-testid={`del-list-${u.user_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Table view */}
        {!loading && view === "table" && filtered.length > 0 && (
          <div className="ios-card overflow-hidden">
            <table className="w-full data-table" data-testid="users-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-[#007AFF]" data-testid="select-all-table" />
                  </th>
                  <th>Nome</th><th>Email</th><th>Ruolo</th><th>Stato Abb.</th><th>Piano</th>
                  <th className="num">Registrato</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const isSelf = u.user_id === user?.user_id;
                  return (
                    <tr key={u.user_id} className={u.blocked ? "bg-rose-50/40" : ""}>
                      <td>
                        {!isSelf && (
                          <input
                            type="checkbox"
                            checked={selected.has(u.user_id)}
                            onChange={() => toggleOne(u.user_id)}
                            className="w-4 h-4 accent-[#007AFF]"
                            data-testid={`select-row-${u.user_id}`}
                          />
                        )}
                      </td>
                      <td className="font-medium">
                        <div className="flex items-center gap-2.5">
                          {u.picture ? (
                            <div className={`relative rounded-full ${u.online ? "shadow-[0_0_0_2px_#34C759,0_0_10px_rgba(52,199,89,0.6)]" : ""}`}>
                              <img src={u.picture} alt="" className="w-7 h-7 rounded-full" />
                            </div>
                          ) : (
                            <div className={`w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-semibold ${u.online ? "shadow-[0_0_0_2px_#34C759,0_0_10px_rgba(52,199,89,0.6)]" : ""}`}>
                              {(u.name || u.email)[0]?.toUpperCase()}
                            </div>
                          )}
                          <span className={u.blocked ? "text-slate-500 line-through" : ""}>{u.name || "—"}</span>
                          {u.online && <span className="text-[9px] uppercase tracking-widest text-emerald-600 font-semibold">● Online</span>}
                        </div>
                      </td>
                      <td className="text-slate-600 text-xs">{u.email}</td>
                      <td>
                        <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.blocked || isSelf}>
                          <SelectTrigger className="rounded-xl h-8 w-32 bg-slate-50 border-0" data-testid={`role-tbl-${u.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin"><div className="flex items-center gap-2"><Shield className="w-3.5 h-3.5" /> Admin</div></SelectItem>
                            <SelectItem value="user"><div className="flex items-center gap-2"><UserIcon className="w-3.5 h-3.5" /> Utente</div></SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td>
                        {u.role === "admin" ? (
                          <Badge className="rounded-full bg-[#12466F] text-white text-[9px] uppercase tracking-widest border-0">Admin</Badge>
                        ) : (
                          <Select value={u.status || "demo"} onValueChange={(v) => changeStatus(u, v)} disabled={u.blocked}>
                            <SelectTrigger className="rounded-xl h-8 w-32 bg-slate-50 border-0" data-testid={`status-tbl-${u.user_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="demo">Demo</SelectItem>
                              <SelectItem value="subscriber">Abbonato</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </td>
                      <td>
                        <Select value={u.tier || "demo"} onValueChange={(v) => changeTier(u, v)} disabled={u.blocked}>
                          <SelectTrigger className="rounded-xl h-8 w-28 bg-slate-50 border-0 uppercase tracking-widest text-xs" data-testid={`tier-tbl-${u.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="demo">demo</SelectItem>
                            <SelectItem value="bronze">bronze</SelectItem>
                            <SelectItem value="silver">silver</SelectItem>
                            <SelectItem value="gold">gold</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td>
                        {u.blocked ? (
                          <Badge className="rounded-full bg-rose-100 text-rose-800 text-[9px] uppercase tracking-widest border-0">Bloccato</Badge>
                        ) : (
                          <Badge className="rounded-full bg-emerald-100 text-emerald-800 text-[9px] uppercase tracking-widest border-0">Attivo</Badge>
                        )}
                      </td>
                      <td className="num text-xs text-slate-500">{(u.created_at || "").slice(0, 10)}</td>
                      <td>
                        {!isSelf && (
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost"
                              className={`rounded-full h-7 px-2 text-xs ${u.blocked ? "text-emerald-700 hover:bg-emerald-50" : "text-amber-700 hover:bg-amber-50"}`}
                              onClick={() => setToBlock(u)}
                              data-testid={`block-tbl-${u.user_id}`}
                            >
                              {u.blocked ? <><RotateCcw className="w-3 h-3 mr-1" />Sblocca</> : <><Ban className="w-3 h-3 mr-1" />Blocca</>}
                            </Button>
                            <Button size="sm" variant="ghost" className="rounded-full text-rose-600 hover:bg-rose-50 h-7 w-7 p-0"
                              onClick={() => setToDelete(u)} data-testid={`del-tbl-${u.user_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Single delete */}
      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare utente?</AlertDialogTitle>
            <AlertDialogDescription>
              "{toDelete?.email}" perderà l'accesso. Le sue schede diventeranno <b>vacanti</b> e potrai riassegnarle o eliminarle.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-rose-600 hover:bg-rose-700" onClick={confirmDelete} data-testid="confirm-del-user">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete */}
      <AlertDialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {selected.size} utenti?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno rimossi permanentemente insieme alle loro sessioni. Le schede possedute diventeranno <b>vacanti</b>.
              Gli admin di sistema verranno saltati automaticamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-rose-600 hover:bg-rose-700" onClick={confirmBulk} data-testid="confirm-bulk-delete">
              Elimina {selected.size} utenti
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Block/unblock */}
      <AlertDialog open={!!toBlock} onOpenChange={(v) => !v && setToBlock(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{toBlock?.blocked ? "Sbloccare" : "Bloccare"} "{toBlock?.email}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {toBlock?.blocked
                ? "L'utente potrà nuovamente accedere con il proprio account Google. Le schede vacanti dovute al blocco torneranno operative."
                : "L'utente non potrà più accedere con il proprio account Google. La sessione attiva verrà chiusa e le sue schede diventeranno vacanti."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction className={`rounded-full ${toBlock?.blocked ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"}`}
              onClick={confirmBlock} data-testid="confirm-block">
              {toBlock?.blocked ? "Sblocca" : "Blocca"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminUsers;
